<script setup lang="ts">
import { computed } from "vue";
import CheckBox from "@/components/Reusables/Inputs/CheckBox.vue";

const props = defineProps<{
  model: { name: string; isActive?: boolean };
  /** One muted line under the name, e.g. when the model last received a version. */
  subline?: string;
  /** A model nothing has been published to yet: dashed outline, so an empty shell cannot pass for geometry. */
  empty?: boolean;
  /**
   * An edit no Save has written yet — a model named on the settings page, or one pulled in from the
   * stream (RTLCA-5766). Tints the whole chip amber instead of the saved green/slate pair.
   */
  pending?: boolean;
}>();

const emit = defineEmits<{
  toggle: [];
}>();

/**
 * Selected chips are tinted green through and through; unselected ones stay on the white ground. A
 * `pending` chip is amber instead of either, and asked first: the first thing to know about a row no
 * Save has written is that it is not there yet, which outranks which side of the toggle it is on
 * (RTLCA-5766). Amber is this page's colour for exactly that — the settings rail's dot, the summary
 * line above the lists, the "To be removed when you save" group — and it is not a warning: red is
 * what invalid input wears here.
 *
 * Dashed for the reason an empty model is: what the chip shows is not the state of things yet. A
 * pending row is empty anyway (nothing can have been published to a model no write has created), so
 * this only makes the tint carry its own outline rather than depend on the caller's `empty`.
 */
const chipClass = computed(() => {
  const dashed = props.pending || props.empty ? "border-dashed" : "";
  if (props.pending) return ["border-app-yellow-300 bg-app-yellow-50 hover:border-app-yellow-400", dashed];
  if (props.model.isActive) return ["border-app-green-300 bg-app-green-50 hover:border-app-green-400", dashed];
  return ["bg-slate-0 hover:border-slate-300 " + (props.empty ? "border-slate-300" : "border-slate-200"), dashed];
});
const tileClass = computed(() => {
  if (props.pending) return "bg-app-yellow-100";
  return props.model.isActive ? "bg-app-green-100" : "bg-slate-100";
});
const nameClass = computed(() => {
  if (props.pending) return "text-app-yellow-900";
  return props.model.isActive ? "text-app-green-900" : "text-type-default";
});
const sublineClass = computed(() => {
  if (props.pending) return "text-app-yellow-700";
  return props.model.isActive ? "text-app-green-700" : "text-type-muted";
});

// The tick still says which side of the toggle a pending model is on — it is a real edit the Save
// will write, flag and all — so only its colour follows the chip.
const checkColor = computed(() => {
  if (!props.model.isActive) return "disabled-parent";
  return props.pending ? "warning-parent" : "positive-parent";
});
</script>

<template>
  <div
    v-if="props.model.isActive !== undefined"
    class="rounded-8 t-small duration-fast ease-standard flex cursor-pointer gap-2 overflow-hidden border pr-3 leading-none transition-colors"
    :class="chipClass"
    data-cy="branch"
    @click="emit('toggle')">
    <div
      class="flex items-center p-2"
      :class="tileClass"
      @click.stop>
      <CheckBox
        size="lg"
        :color="checkColor"
        :value="props.model.isActive ?? false"
        :aria-label="props.model.name"
        @update:value="() => emit('toggle')" />
    </div>

    <span class="flex flex-col justify-center gap-0.5 py-1.5 select-none">
      <span :class="nameClass">{{ props.model.name }}</span>
      <span
        v-if="props.subline"
        class="t-caption"
        :class="sublineClass"
        >{{ props.subline }}</span
      >
    </span>

    <!-- A caller's own control at the chip's end. Its clicks are its own, not a toggle. -->
    <span
      v-if="$slots.action"
      class="flex items-center"
      @click.stop>
      <slot name="action" />
    </span>
  </div>
</template>
